import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi, type Source } from "../lib/api";
import Hint from "../components/Hint";
import { useTenant } from "../lib/tenant";

type ConnectorTemplate = {
  type: Source["type"];
  label: string;
  icon: string;
  desc: string;
  configFields: Array<{ key: string; label: string; placeholder?: string; type?: string; secret?: boolean }>;
};

const TEMPLATES: ConnectorTemplate[] = [
  {
    type: "upload",
    label: "CSV / Excel Upload",
    icon: "📁",
    desc: "Lade eine CSV- oder Excel-Datei hoch. Der Discovery-Agent erkennt automatisch Spalten + Datentypen.",
    configFields: [
      { key: "file_name", label: "Dateiname", placeholder: "z.B. q1_revenue.csv" },
      { key: "delimiter", label: "CSV-Trennzeichen", placeholder: ",", type: "text" },
      { key: "has_header", label: "Erste Zeile = Header (true/false)", placeholder: "true", type: "text" },
    ],
  },
  {
    type: "rest",
    label: "Stripe Sandbox",
    icon: "💳",
    desc: "Stripe-Test-Mode-API. Liefert Charges, Subscriptions, Invoices als Demo-Daten.",
    configFields: [
      { key: "api_key", label: "Stripe Test-API-Key", placeholder: "sk_test_...", secret: true },
      { key: "scope", label: "Resource-Scope (comma)", placeholder: "charges,subscriptions,invoices" },
    ],
  },
  {
    type: "crawl4ai",
    label: "Crawl4AI",
    icon: "🕷️",
    desc: "Web-Crawler für Marktbenchmarks, Wettbewerbsanalyse, Branchenreports.",
    configFields: [
      { key: "base_url", label: "Crawl4AI Endpoint", placeholder: "https://crawl.ben-e-fit.ai" },
      { key: "seed_urls", label: "Seed URLs (comma)", placeholder: "https://example.com/blog" },
      { key: "depth", label: "Max Crawl-Tiefe", placeholder: "2", type: "number" },
    ],
  },
  {
    type: "nimble",
    label: "Nimble Marketing",
    icon: "📊",
    desc: "Marketing-Daten von Nimble: Ads, Social, Web-Traffic-Funnel.",
    configFields: [
      { key: "api_key", label: "Nimble API Key", placeholder: "nim_...", secret: true },
      { key: "workspace_id", label: "Nimble Workspace-ID", placeholder: "ws_..." },
    ],
  },
];

/** Deutsche, ehrliche Übersetzung der technischen Quell-Status. */
const SOURCE_STATUS_DE: Record<string, string> = {
  healthy: "in Ordnung", degraded: "eingeschränkt", failed: "gestört", unknown: "unbekannt",
};

type TrustCheck = { id: string; status: string; title: string; detail: string; release_blocking?: boolean };
type TrustStatus = {
  status?: string; checked_at?: string; trusted_source_count?: number; source_count?: number;
  quality_threshold?: number; checks?: TrustCheck[];
};

export default function SourcesPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<ConnectorTemplate | null>(null);
  const [name, setName] = useState("");
  const [config, setConfig] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const list = useQuery({
    queryKey: ["sources", effectiveTenantId],
    queryFn: () => api.get<Source[]>("/sources"),
    refetchInterval: 30_000,
  });

  const trust = useQuery({
    queryKey: ["data-trust", effectiveTenantId],
    queryFn: () => api.get<TrustStatus>("/data-trust/status"),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<Source>("/sources", {
        type: picked!.type,
        name,
        config,
      }),
    onSuccess: () => {
      setPicked(null);
      setName("");
      setConfig({});
      qc.invalidateQueries({ queryKey: ["sources", effectiveTenantId] });
    },
  });

  const sync = useMutation({
    mutationFn: (sourceId: string) => api.post<{ run_id: string }>(`/sources/${sourceId}/sync`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources", effectiveTenantId] }),
  });

  return (
    <div className="page sources">
      <h1>Datenquellen &amp; Vertrauensprüfung</h1>
      <p className="subtitle">
        Hier entscheidet sich, ob das Cockpit Zahlen zeigen darf. Jede Quelle wird geprüft
        (erreichbar? aktuell? ausreichend gefüllt?) und bekommt einen <Hint term="quality_score">Quality Score</Hint>.
        Erst wenn mindestens eine Quelle die Schwelle von 75 Punkten erreicht, öffnet das{" "}
        <Hint term="trust_gate">Data-Trust-Gate</Hint> die Kennzahlen. Über „Quality Gate" sehen Sie je
        Quelle im Detail, warum sie grün oder rot ist.
      </p>

      <section>
        <h2>
          Vertrauens-Checkliste{" "}
          {trust.data?.status && (
            <span className={`status-pill ${trust.data.status === "trusted" ? "good" : "warning"}`}>
              {trust.data.status === "trusted" ? "vertrauenswürdig" : trust.data.status}
            </span>
          )}
        </h2>
        <p className="muted small">
          Diese Prüfungen entscheiden, ob das Data-Trust-Gate die Kennzahlen freigibt
          ({trust.data?.trusted_source_count ?? 0}/{trust.data?.source_count ?? 0} Quellen über der Schwelle
          {trust.data?.quality_threshold != null ? ` von ${trust.data.quality_threshold} Punkten` : ""}).
        </p>
        {trust.isLoading && <div className="skeleton" style={{ height: 60 }} aria-label="Checkliste wird geladen" />}
        {trust.isError && (
          <div className="state-error"><strong>Checkliste derzeit nicht abrufbar.</strong>
            <span className="muted small">Die Quellenliste unten funktioniert unabhängig davon.</span></div>
        )}
        {(trust.data?.checks?.length ?? 0) > 0 && (
          <ul className="trust-checklist">
            {trust.data!.checks!.map((c) => {
              const cls = c.status === "pass" || c.status === "ok" ? "pass" : c.status === "fail" ? "fail" : "warn";
              return (
                <li key={c.id} className={cls}>
                  <span className="tcl-icon" aria-hidden>{cls === "pass" ? "✓" : cls === "fail" ? "✕" : "!"}</span>
                  <div className="tcl-copy">
                    <strong>{c.title}</strong>
                    <span>{c.detail}</span>
                  </div>
                  {c.release_blocking && <span className="tcl-block" title="Ohne diese Prüfung werden keine Kennzahlen angezeigt">release-kritisch</span>}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <h2>Vorhandene Quellen ({list.data?.length ?? 0})</h2>
        {list.isLoading && <p className="muted">Lädt…</p>}
        {list.data && list.data.length === 0 && (
          <div className="empty">
            <p className="muted">Noch keine Quellen verbunden. Wähle unten einen Connector aus.</p>
          </div>
        )}
        <ul className="metric-list">
          {(list.data ?? []).map((s) => (
            <li key={s.id} className="metric-item source-item">
              <div className="source-main">
                <strong>{s.name}</strong>
                <code className="muted small"> · {s.type} · {s.id}</code>
              </div>
              <div className="source-meta">
                <span className={`status-pill ${s.status}`} title={s.status}>
                  {SOURCE_STATUS_DE[s.status ?? "unknown"] ?? s.status ?? "unbekannt"}
                </span>
                {s.last_sync && (
                  <span className="muted small">
                    Zuletzt geprüft: {new Date(s.last_sync).toLocaleString("de-DE")}
                  </span>
                )}
                <button
                  className="btn ghost small"
                  onClick={() => sync.mutate(s.id)}
                  disabled={sync.isPending}
                  title="Markiert die Quelle als geprüft und aktualisiert den Zeitstempel. Der eigentliche Datenabzug läuft zeitgesteuert im Hintergrund (OpenProject-Sync, Lakehouse-Refresh) — nicht durch diesen Knopf."
                >
                  {sync.isPending ? "Prüfe …" : "Status aktualisieren"}
                </button>
                <Link className="btn small" to={`/app/sources/${s.id}/quality`}>
                  Quality Gate
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2>Neue Quelle verbinden</h2>
        <p className="muted small">
          Hinweis: Die folgenden Vorlagen registrieren eine Quelle mit ihren Zugangsdaten. Ein produktiver
          Datenabzug existiert heute nur für die bereits angeschlossenen Systeme (OpenProject, OpenSearch,
          Lakehouse-Gold). Neue Vorlagen legen die Quelle an, holen aber noch keine Daten.
        </p>
        {!picked && (
          <div className="connector-grid">
            {TEMPLATES.map((t) => (
              <button
                key={t.type + t.label}
                className="connector-card"
                onClick={() => {
                  setPicked(t);
                  setName(t.label);
                }}
              >
                <div className="connector-icon">{t.icon}</div>
                <div className="connector-label">{t.label}</div>
                <div className="connector-desc muted small">{t.desc}</div>
              </button>
            ))}
          </div>
        )}

        {picked && (
          <div className="connector-form">
            <div className="connector-form-hdr">
              <span className="connector-icon">{picked.icon}</span>
              <span><strong>{picked.label}</strong> konfigurieren</span>
              <button className="btn ghost small" onClick={() => setPicked(null)}>Abbrechen</button>
            </div>

            <label>
              <span>Name dieser Quelle</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={picked.label} />
            </label>

            {picked.configFields.map((f) => (
              <label key={f.key}>
                <span>{f.label}</span>
                <input
                  type={f.secret ? "password" : f.type ?? "text"}
                  value={config[f.key] ?? ""}
                  onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                />
              </label>
            ))}

            {picked.type === "upload" && (
              <label>
                <span>Datei auswählen</span>
                <input
                  type="file"
                  ref={fileRef}
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setConfig({
                        ...config,
                        file_name: f.name,
                        file_size_bytes: String(f.size),
                      });
                      if (!name || name === picked.label) setName(f.name);
                    }
                  }}
                />
                <small className="muted">
                  Hinweis: Im Demo-Modus wird die Datei nicht hochgeladen, nur registriert. Der echte
                  Upload-Endpoint kommt mit der nächsten Iteration. Du kannst aber bereits Metadaten
                  speichern (Dateiname, Größe, Trennzeichen).
                </small>
              </label>
            )}

            <div className="form-nav">
              <button
                className="btn primary"
                onClick={() => create.mutate()}
                disabled={create.isPending || !name}
              >
                {create.isPending ? "Erzeuge…" : "Quelle anlegen"}
              </button>
            </div>

            {create.error && (
              <p className="error">Fehler: {(create.error as { message?: string }).message}</p>
            )}
            {create.isSuccess && (
              <p className="success">
                Quelle angelegt. Sync läuft automatisch in den nächsten Minuten und der
                kpi-discovery-Agent analysiert die Daten.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
